import { useEffect, useRef, useState } from 'react'

/**
 * The rendered size of an element, kept current.
 *
 * Fourteen lines instead of a charting library's reactive measurement, which
 * is most of what one buys. `ResizeObserver` is in every browser the app
 * targets; the initial size is read synchronously so the first paint is not a
 * zero-width chart.
 */
export function useElementSize<T extends HTMLElement>(): [
  React.RefObject<T | null>,
  { width: number; height: number },
] {
  const ref = useRef<T | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const element = ref.current
    if (element === null) return
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, size]
}
